import cn from 'classnames';
import { addDays, differenceInMilliseconds, parseISO } from 'date-fns';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import refreshClock from 'utils/refreshClock';
import styles from './Countdown.module.scss';
import CountdownItem from './CountdownItem';

const COUNTDOWN_CLOSED_KEY = 'countdownClosed';

interface OwnProps {
  countdown: {
    endDate: string;
    endDateColor: string;
    endDateLabelsColor: string;
    heading: string;
    headingColor: string;
    subheading: string;
    subheadingColor: string;
    backgroundColor: string;
    greeting: string;
  };
}

interface Time {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const getCountdownTimeDiff = (endDate: string) => {
  const now = new Date().getTime();
  const countdownDate = parseISO(endDate).getTime();
  const timeDiff = differenceInMilliseconds(countdownDate, now);
  return timeDiff;
};

const Countdown: FC<OwnProps> = ({
  countdown: {
    backgroundColor,
    endDate,
    endDateColor,
    endDateLabelsColor,
    heading,
    headingColor,
    subheading,
    subheadingColor,
    greeting,
  },
}) => {
  const { t } = useTranslation();
  const [time, setTime] = useState<Time | undefined>(undefined);
  const [countdownFinished, setCountdownFinished] = useState<boolean>(false);
  const [showCountdown, setShowCountdown] = useState<boolean>(getCountdownTimeDiff(endDate) > 0);

  useEffect(() => {
    const countdownCloseTimestamp = localStorage.getItem(COUNTDOWN_CLOSED_KEY);

    if (countdownCloseTimestamp) {
      const countdownCloseDate = parseISO(countdownCloseTimestamp);
      const now = new Date();

      if (now <= addDays(countdownCloseDate, 1)) {
        setShowCountdown(false);
      } else {
        localStorage.removeItem(COUNTDOWN_CLOSED_KEY);
      }
    }

    const timerId = setInterval(() => {
      const timeDiff = getCountdownTimeDiff(endDate);
      if (timeDiff <= 0 && !countdownFinished) {
        setCountdownFinished(true);
        clearInterval(timerId);
      }

      setTime(refreshClock(timeDiff));
    }, 1000);

    return function cleanUp() {
      clearInterval(timerId);
    };
  }, []);

  if (!showCountdown || countdownFinished) {
    return null;
  }

  const handleCloseCountdown = () => {
    const currentTimestamp = new Date();
    localStorage.setItem(COUNTDOWN_CLOSED_KEY, currentTimestamp.toString());
    setShowCountdown(false);
  };

  return (
    <div className={styles.countdown} style={{ backgroundColor }}>
      {!countdownFinished && (
        <>
          <div className={styles.countdownContent}>
            <div className={styles.countdownNumbersContainer}>
              {time && (
                <>
                  <CountdownItem
                    label={t('txt_days')}
                    value={time.days}
                    numbersColor={endDateColor}
                    labelColor={endDateLabelsColor}
                  />
                  <CountdownItem
                    label={t('txt_hours')}
                    value={time.hours}
                    numbersColor={endDateColor}
                    labelColor={endDateLabelsColor}
                  />
                  <CountdownItem
                    label={t('txt_mins')}
                    value={time.minutes}
                    numbersColor={endDateColor}
                    labelColor={endDateLabelsColor}
                  />
                  <CountdownItem
                    label={t('txt_seconds')}
                    value={time.seconds}
                    numbersColor={endDateColor}
                    labelColor={endDateLabelsColor}
                  />
                </>
              )}
            </div>
            <div className={styles.countdownText}>
              {heading && <h2 style={{ color: headingColor }}>{heading}</h2>}
              {subheading && <p style={{ color: subheadingColor }}>{subheading}</p>}
            </div>
          </div>
          <div onClick={handleCloseCountdown} className={cn(styles.op_icon, styles.op_icon_cross)} />
        </>
      )}
      {countdownFinished && <div className={styles.countdownGreeting}>{greeting}</div>}
    </div>
  );
};

export default Countdown;
